import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Divider
} from '@nextui-org/react'
import { useTranslation } from 'react-i18next'

import { SubTitle, Text } from '../common/UI'

import { InputControl } from './InputControl'
import { SelectControl } from './SelectControl'

import { useAddressEditor } from '@/hooks/useAddressEditor'
import { AddressEditorProps } from '@/types/forms/addressForm'
import { Icons } from '@/components/icons'
import { useRef, useState, useEffect } from 'react'

// Component for address preview display
const AddressPreview = ({ addressPreview }: { addressPreview: string }) => {
    const { t } = useTranslation()

    return (
        <div className="px-6 py-2 bg-primary/5 dark:bg-primary/10 border-b border-primary/10 transition-all duration-300">
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 shrink-0">
                    <Icons.Location className="h-4 w-4 text-primary" />
                    <Text
                        text={t('address.preview')}
                        className="text-[10px] font-black text-primary uppercase tracking-widest hidden sm:block"
                    />
                </div>
                <div className="flex-1 bg-white/50 dark:bg-gray-900/50 px-3 py-1.5 rounded-lg border border-primary/20 shadow-inner">
                    <Text
                        text={addressPreview || t('address.previewEmpty')}
                        className={`text-sm font-bold truncate ${addressPreview ? 'text-gray-800 dark:text-gray-100' : 'text-gray-400 italic'}`}
                    />
                </div>
            </div>
        </div>
    )
}

// Component for main road information section
const MainRoadSection = ({ control, addressOptions, validationRules }: any) => {
    const { t } = useTranslation()

    return (
        <div 
            id="main-road" 
            className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm transition-all snap-start scroll-mt-4"
        >
            <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-primary rounded-full"></div>
                <SubTitle
                    subTitle={t('address.mainRoad')}
                    className="text-primary text-base font-bold"
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <SelectControl
                    name="roadType"
                    control={control}
                    rules={validationRules.roadType}
                    options={addressOptions.roadTypes}
                    label={t('address.roadType')}
                    placeholder={t('address.roadType')}
                    className="w-full"
                    size="sm"
                    isRequired
                />
                <InputControl
                    name="roadNumber"
                    control={control}
                    rules={validationRules.roadNumber}
                    label={t('address.roadNumber')}
                    placeholder={t('address.roadNumber')}
                    typeInput="number"
                    allowedPattern={/^[0-9]*$/}
                    size="sm"
                    isRequired
                />
                <SelectControl
                    name="roadAppendix"
                    control={control}
                    options={addressOptions.appendices}
                    label={t('address.appendix')}
                    placeholder={t('address.appendix')}
                    className="w-full"
                    size="sm"
                />
                <SelectControl
                    name="roadOrientation"
                    control={control}
                    options={addressOptions.orientations}
                    label={t('address.orientation')}
                    placeholder={t('address.orientation')}
                    className="w-full"
                    size="sm"
                />
            </div>
        </div>
    )
}

// Component for cross number information section
const CrossNumberSection = ({
    control,
    addressOptions,
    validationRules,
    validateCrossNumber
}: any) => {
    const { t } = useTranslation()

    return (
        <div 
            id="cross-number" 
            className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm transition-all snap-start scroll-mt-4"
        >
            <div className="flex items-center gap-2 mb-1">
                <div className="w-1 h-5 bg-primary rounded-full"></div>
                <SubTitle
                    subTitle={t('address.crossNumber')}
                    className="text-primary text-base font-bold"
                />
            </div>
            <Text
                text={t('address.crossNumberDescription')}
                className="text-xs text-gray-500 mb-4 ml-3"
            />

            <div className="flex flex-col md:flex-row items-center gap-2 [&_.input-wrapper]:mb-0">
                <div className="flex items-center justify-center bg-primary/10 rounded-xl min-w-[2.8rem] h-[48px] border border-primary/20">
                    <span className="text-xl font-black text-primary">#</span>
                </div>

                <div className="flex-1 w-full">
                    <InputControl
                        name="crossNumber"
                        control={control}
                        rules={{
                            ...validationRules.crossNumber,
                            validate: validateCrossNumber
                        }}
                        label={t('address.roadNumber')}
                        placeholder={t('address.roadNumber')}
                        typeInput="number"
                        allowedPattern={/^[0-9]*$/}
                        size="sm"
                        isRequired
                    />
                </div>

                <div className="flex-1 w-full">
                    <SelectControl
                        name="crossAppendix"
                        control={control}
                        options={addressOptions.appendices}
                        label={t('address.appendix')}
                        placeholder={t('address.appendix')}
                        size="sm"
                    />
                </div>

                <div className="flex-1 w-full">
                    <SelectControl
                        name="crossOrientation"
                        control={control}
                        options={addressOptions.orientations}
                        label={t('address.orientation')}
                        placeholder={t('address.orientation')}
                        size="sm"
                    />
                </div>

                <div className="flex items-center justify-center bg-primary/10 rounded-xl min-w-[2.8rem] h-[48px] border border-primary/20">
                    <span className="text-xl font-black text-primary">-</span>
                </div>

                <div className="flex-1 w-full">
                    <InputControl
                        name="additionalNumber"
                        control={control}
                        rules={validationRules.additionalNumber}
                        label={t('address.additionalNumber')}
                        placeholder={t('address.additionalNumber')}
                        typeInput="number"
                        allowedPattern={/^[0-9]*$/}
                        size="sm"
                        isRequired={!!validationRules.additionalNumber?.required}
                    />
                </div>
            </div>
        </div>
    )
}

// Component for address complement section
const ComplementSection = ({ control }: any) => {
    const { t } = useTranslation()

    return (
        <div 
            id="complement" 
            className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm transition-all text-sm snap-start scroll-mt-4"
        >
            <div className="flex items-center gap-2 mb-1">
                <div className="w-1 h-5 bg-primary rounded-full"></div>
                <SubTitle
                    subTitle={t('address.complement')}
                    className="text-primary text-base font-bold"
                />
            </div>
            <Text
                text={t('address.complementDescription')}
                className="text-xs text-gray-500 mb-4 ml-3"
            />

            <InputControl
                name="complement"
                control={control}
                label={t('address.complement')}
                placeholder={t('address.complementPlaceholder')}
                allowedPattern={/^[a-zA-Z0-9\s\-\.#°]*$/}
                maxWords={100}
                className="w-full"
                size="sm"
            />
        </div>
    )
}

// Component for modal content
const AddressEditorModal = ({
    isOpen,
    title,
    isLoading,
    hasData,
    addressPreview,
    control,
    addressOptions,
    validationRules,
    validateCrossNumber,
    handleSave,
    handleCancel,
    handleClear,
    t
}: any) => {
    const modalBodyRef = useRef<HTMLDivElement>(null)

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            size="4xl"
            placement="center"
            backdrop="blur"
            scrollBehavior="inside"
            classNames={{
                base: 'max-h-[85vh] rounded-2xl overflow-hidden',
                header: 'border-b border-gray-100 dark:border-gray-800 bg-white/50 dark:bg-gray-900/50 backdrop-blur-md py-3 px-6',
                footer: 'border-t border-gray-100 dark:border-gray-800 bg-white/50 dark:bg-gray-900/50 backdrop-blur-md py-3 px-6'
            }}
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader>
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-primary/10 rounded-lg shrink-0">
                                    <Icons.Home className="h-4 w-4 text-primary" />
                                </div>
                                <SubTitle
                                    subTitle={t(title)}
                                    className="text-primary text-base font-black"
                                />
                            </div>
                        </ModalHeader>

                        <AddressPreview addressPreview={addressPreview} />

                        <ModalBody className="p-0 bg-gray-50/30 dark:bg-gray-950/30 overflow-hidden">
                            <div 
                                ref={modalBodyRef} 
                                className="p-6 h-full overflow-y-auto snap-y snap-mandatory scroll-smooth"
                            >
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="relative w-12 h-12">
                                            <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                                            <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                                        </div>
                                        <Text
                                            text={t('loading')}
                                            className="mt-3 text-primary font-bold animate-pulse"
                                        />
                                    </div>
                                ) : !hasData ? (
                                   <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <Text
                                            text="No se pudieron cargar los datos de direcciones"
                                            className="text-base font-bold text-gray-800 dark:text-gray-200"
                                        />
                                        <Button
                                            variant="flat"
                                            color="primary"
                                            size="sm"
                                            className="mt-4"
                                            onPress={() => window.location.reload()}
                                        >
                                            Reintentar carga
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-[10vh] pb-[20vh]">
                                        <MainRoadSection
                                            control={control}
                                            addressOptions={addressOptions}
                                            validationRules={validationRules}
                                        />
                                        
                                        <CrossNumberSection
                                            control={control}
                                            addressOptions={addressOptions}
                                            validationRules={validationRules}
                                            validateCrossNumber={validateCrossNumber}
                                        />
                                        
                                        <ComplementSection control={control} />
                                    </div>
                                )}
                            </div>
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                variant="flat"
                                color="danger"
                                onPress={handleClear}
                                size="sm"
                                className="font-bold rounded-lg"
                                startContent={<Icons.Times className="h-4 w-4" />}
                            >
                                {t('address.clear')}
                            </Button>

                            <div className="flex gap-2">
                                <Button
                                    variant="light"
                                    color="default"
                                    size="sm"
                                    onPress={handleCancel}
                                    className="font-bold rounded-lg"
                                >
                                    {t('common.cancel')}
                                </Button>

                                <Button
                                    color="primary"
                                    size="sm"
                                    onPress={handleSave}
                                    className="font-black px-6 rounded-lg shadow-md shadow-primary/20"
                                    isDisabled={
                                        !addressPreview.trim() ||
                                        isLoading ||
                                        !hasData
                                    }
                                    startContent={
                                        <Icons.Check className="h-4 w-4" />
                                    }
                                >
                                    {t('common.save')}
                                </Button>
                            </div>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    )
}

export const AddressEditor = ({
    isOpen,
    onClose,
    onSave,
    initialValue,
    title = 'address.editTitle',
    department
}: AddressEditorProps): JSX.Element => {
    const { t } = useTranslation()

    const {
        form,
        control,
        addressOptions,
        validationRules,
        addressPreview,
        generateAddressString,
        clearForm,
        validateCrossNumber,
        getValues,
        isLoading,
        hasData,
        resetForm
    } = useAddressEditor(initialValue, department)

    // Reset form data when modal opens with initial value
    useEffect(() => {
        if (isOpen && initialValue && hasData) {
            resetForm(initialValue)
        }
    }, [isOpen, initialValue, hasData, resetForm])

    const { handleSubmit } = form

    const handleSave = () => {
        handleSubmit((data) => {
            const addressString = generateAddressString(data)

            onSave(addressString)
            onClose()
        })()
    }

    const handleCancel = () => {
        clearForm()
        onClose()
    }

    const handleClear = () => {
        clearForm()
    }

    return (
        <AddressEditorModal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            isLoading={isLoading}
            hasData={hasData}
            addressPreview={addressPreview}
            control={control}
            addressOptions={addressOptions}
            validationRules={validationRules}
            validateCrossNumber={validateCrossNumber}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleClear={handleClear}
            t={t}
        />
    )
}
